<template>
  <DynamicStepper
    v-bind="stepperProps"
    :inputProperties="resultInputProperties"
    :enableFinalSubmit="true"
    finalSubmitTitle="uiSignupRegistration.register"
    :altLabels="altLabels"
  >
    <v-alert
      type="success"
      class="mb-4"
    >
      <div v-if="loginCredentialsSent">{{ $t('uiSignupRegistration.check_email') }}</div>
      <div v-else>{{ $t('uiSignupRegistration.you_have_registered') }}</div>
    </v-alert>
  </DynamicStepper>
</template>

<script setup lang="ts">
import { auth } from '@ui-api3-sdk/api';

import {computed, markRaw, onMounted, ref} from 'vue';
import { SignupRegistrationProps, defaults } from './SignupRegistration.ts';

import { useFormBuilder } from '@/composables';
import { apiErrorToString, useAccountCreationApi, useAccountSearchApi } from '@ui-api3-sdk/api/api3';

import { untilFalse } from '@/utils/reactive-helpers';

import { Step } from '@/models/dynamic-stepper-interface';
import { CONFIRMABLE_FIELDS_ALIASES, ConfirmableFieldAlias, ConfirmationResult } from '@/models/field-confirmation-interface';
import { FormResult, genFormFieldFromAlias } from '@/models/form-builder-interface';

import { useConfirmableField } from '@/composables/useConfirmableField';
import { useDynamicStepper } from '@/composables/useDynamicStepper';
import { useNavigation } from '@/composables/useNavigation';

import { LoginType, PasswordCreationType, loginTypeFromName, useEnrollSettings } from '@/composables/useEnrollSettings';
import { getLoginTypeString } from '@/utils/utils';

import { useLocalization } from '@/composables/useLocalization';

import { usePropertiesStore } from '@/pinia/usePropertiesStore';
import { useRuntimeSettingsStore } from '@/pinia/useRuntimeSettingsStore.ts';

import AgreementCheckbox from './base/AgreementCheckbox.vue';
import DynamicStepper from './base/DynamicStepper.vue';
import { LinkBoxProps } from './LinkBox.ts';

const { t } = useLocalization('uiSignupRegistration');

const { isLoading } = useLocalization('fieldConfirmation', [
  'not_unique',
  'error_checking_uniqueness',
]);

const props = withDefaults(defineProps<SignupRegistrationProps>(), {
  inputProperties: undefined,
  fieldTitles: undefined,
  optionalFields: undefined,
  forceFields: undefined,
  agreementWarning: '',
  steps: () => defaults.steps,
  otpConfirm: () => defaults.otpConfirm,
  autoLogin: () => defaults.autoLogin,
  languageMode: () => defaults.languageMode,
  agreementLinkboxProps: () => defaults.agreementLinkboxProps,
  loginType: undefined,
});

const otpHeaders = new Map<ConfirmableFieldAlias, ConfirmationResult>();
const { settings: enrollSettings, inputProperties: resultInputProperties } = useEnrollSettings(props.inputProperties);
const { stepperProps, stepperMethods: stepper } = useDynamicStepper([], onFinalSubmit);
const loginCredentialsSent = ref(false);
const languagesFromApi = ref<Array<{ key: string; value: string }>>([]);

onMounted(async () => {
  await untilFalse(isLoading);
  await loadLanguages();
  await tryLoadProperties();

  assertPropsValid();
  setupSteps();
  setupLanguageField();
});

async function tryLoadProperties() {
  try {
    await usePropertiesStore().getPfAsync();
  } catch (err) {
    console.warn('Profile fields cant be fetched, dictionaries are impossible');
  }
}

async function loadLanguages() {
  try {
    // Fetch via store and read
    await usePropertiesStore().fetchLanguagesList();
    const list = usePropertiesStore().getLanguagesList();
    languagesFromApi.value = list;
  } catch (err) {
    console.warn('Languages list cant be fetched', err);
  }
}

function setupSteps() {
  addFormSteps();

  if (props.agreementFiles?.length) {
    addAgreementStep();
  }

  addConfirmationSteps();
  updateSponsorField();
}


function getFormValues(): Record<string, any> {
  const profile = Object.fromEntries(
    Object.entries(stepper.getAllFormsValues()).filter(([_, v]) => v),
  );

  Object.entries(props.forceFields || {}).forEach(([k, v]) => {
    profile[k] = v;
  });

  return profile;
}

async function onFinalSubmit() {

  const profile = getFormValues();
  ['password', 'password_confirm', 'sponsor_id', 'login'].forEach( a => delete profile[a] );

  const gluedOtpHeaders = Array.from(otpHeaders.values())
    .map( h => h.otpHeader['x-otp-check'] )
    .join('|');

  try {

    let login = getLoginValue();
    const password = getPasswordValue();

    if ( (!login && !idAsLoginMode()) || password === null) {
      const effectiveLoginType = getEffectiveLoginType();
      console.log('Login:', login, 'Effective login type:', effectiveLoginType, 'Enroll login type:', enrollSettings.value.loginType, 'Password:', password, 'Enroll password type:', enrollSettings.value.passwordCreationType);
      throw new Error('Please check login configuration');
    }

    const createResult = await useAccountCreationApi()
      .accountSignupControllerCreateAccount({
        accountSignupReq: {
          login,
          sponsorId: stepper.findFormField('sponsor_id')?.value,
          password,
          profile: withLanguageInProfile(profile),
          loginType: props.loginType?.toLocaleLowerCase() || getLoginTypeString(enrollSettings.value.loginType).toLocaleLowerCase(),
        },
      }, {
        headers: { 'x-otp-check': gluedOtpHeaders },
      });


    if (idAsLoginMode()) {
      login = String((createResult.data.payload.info as any)?.account?.id); // or user.id ?
      if (! login) {
        console.error('No id as login in response:', createResult);
      }
    }

    if (createResult.status === 200) {

      if (props.autoLogin && enrollSettings.value.passwordCreationType === PasswordCreationType.BY_USER) {
        if (!login || !password) {
          throw new Error('Auto login is not possible without login and password specified by user');
        }
        const loginType = getLoginTypeString(getEffectiveLoginType());
        const loginRes = await auth.login(login, password, true, loginType);
        if (loginRes) {
          const gotTo = useRuntimeSettingsStore().getLoginRedirectPath || '/';
          useRuntimeSettingsStore().setLoginRedirectPath('');
          useNavigation().go(gotTo);
          return undefined;
        }
      }
      if(enrollSettings.value.passwordCreationType === PasswordCreationType.AUTO) {
        loginCredentialsSent.value = true;
        return undefined;
      }
      useNavigation().toLogin();
      return undefined;
    }


  } catch (err: any) {
    return apiErrorToString(err, 'Error creating account');
  }

  return 'Error creating account';
}

function getEffectiveLoginType(): LoginType {
  if (props.loginType !== undefined) {
    return loginTypeFromName(props.loginType);
  }
  return enrollSettings.value.loginType;
}

// Функция для форматирования номера телефона
function formatPhoneNumber(phone: string): string {
  if (!phone) return phone;

  // Убираем все символы кроме цифр
  const digits = phone.replace(/[^\d]/g, '');

  // Добавляем знак + в начало
  return `+${digits}`;
}

function getLoginValue() {

  const mode = getEffectiveLoginType();

  switch (mode) {
    case LoginType.ID_AS_LOGIN: return stepper.findFormField('login')?.value;
    case LoginType.CUSTOM_LOGIN: return stepper.findFormField('login')?.value;
    case LoginType.PHONE_AS_LOGIN: {
      const phone = stepper.findFormField('phone')?.value;
      return phone ? formatPhoneNumber(phone) : phone;
    }
    default: return stepper.findFormField('email')?.value;
  }
}

function idAsLoginMode() {
  return getEffectiveLoginType() === LoginType.ID_AS_LOGIN;
}


function getPasswordValue() {

  const passwordByUser = stepper.findFormField('password');

  if (enrollSettings.value.passwordCreationType === PasswordCreationType.BY_USER) {

    if (!passwordByUser?.value) {
      console.error('Enroll settings have PASSWORD_BY_USER set, but no password provided in form!');
      return null;
    }

    return passwordByUser.value;
  }

  if (passwordByUser?.value) {
    console.error('Enroll settings PASSWORD_AUTO is set, but password provided by user!');
    return null;
  }


  return undefined;

}

async function onFieldConfirmedStepGuard(result: ConfirmationResult) {
  if (result) {
    otpHeaders.set(result.field, result);
  }
  return true;
}

async function onFormSubmitStepGuard(formRes: FormResult, step: Step) {
  if (step.type !== 'FormBuilder') throw new Error('no way');
  const promises = [];


  for (const alias of CONFIRMABLE_FIELDS_ALIASES) {
    if ( formRes[alias] ) {
      const cf = useConfirmableField(alias);
      promises.push(cf.checkUniqueness(formRes[alias], step.form));
    }
  }

  if (! promises.length) return true;

  const results = await Promise.all(promises);
  return ! results.some( (r: boolean) => !r );
}


function addAgreementStep() {

  const componentProps = {
    linkbox: {
      ... defaults.agreementLinkboxProps,
      ...(props.agreementLinkboxProps || {}),
      files: props.agreementFiles,
    } as LinkBoxProps,
    agreementWarning: props.agreementWarning,
    agreeLabel: props.agreementTitle,
    agreeValidationMessage: props.agreementValidationText,
    formFieldsGetter: getFormValues,
  };

  stepper.addStep({
    type: 'CustomComponent',
    title: `uiSignupRegistration.agreement_title`,
    component: markRaw(AgreementCheckbox),
    name: 'agreement',
    componentProps,
    isMandatory: true,
  });

}

function addFormSteps() {
  props.steps.forEach(step => {

    const fields = step.aliases.map(alias => {
      const field = {
        ... genFormFieldFromAlias(alias),
        isMandatory: ! isFieldOptional(alias),
      };
      if (props.fieldTitles?.[alias]) {
        field.title = props.fieldTitles[alias];
      }
      if (alias === 'language_id') {
        // enforce visibility/editability according to languageMode
        if (props.languageMode === 'hidden_no_api' || props.languageMode === 'hidden_auto') {
          field.hidden = true;
          field.disabled = true;
        } else if (props.languageMode === 'editable_auto') {
          field.hidden = false;
          field.disabled = false;
        }
      }
      return field;
    });

    stepper.addStep({
      name: step.title,
      type: 'FormBuilder',
      form: useFormBuilder(fields),
      goNextGuard: onFormSubmitStepGuard,
    });

  });

}

function addConfirmationSteps() {
  const fieldsToConfirm = CONFIRMABLE_FIELDS_ALIASES
    .filter(alias => props.otpConfirm[alias])
    .sort( (alias1, alias2) => {
      const a = props.otpConfirm[alias1];
      const b = props.otpConfirm[alias2];
      if (a === b) return 0;
      if (a === 'optional') return -1;
      return 1;
    }); // confirmed fields must come last, optinally confirmed - first

  fieldsToConfirm.forEach(alias => {
    const formField = stepper.findFormField(alias);
    if (! formField) return;

    stepper.addStep({
      type: 'FieldConfirmation',
      name: `${alias}_confirm`,
      title: `uiSignupRegistration.${alias}_confirmation`,
      alias,
      valueRef: computed(() => {
        const val = stepper.findFormField(alias)?.value;
        return val;
      }),
      isMandatory: ! isFieldOptional(alias),
      goNextGuard: onFieldConfirmedStepGuard,
      componentProps: {
        updateProfile: false,
      },
    });
  });

}

function isFieldOptional(alias: string) {
  return props.optionalFields?.includes(alias);
}

async function updateSponsorField() {

  const sponsorField = stepper.findFormField('sponsor_id');
  if (! sponsorField) return;

  const codeInUrl = useNavigation().param('ref');
  const codeInLocalStorage = localStorage.getItem('referral_code');

  if (codeInUrl) {
    localStorage.setItem('referral_code', codeInUrl);
  }

  const inviteCode = codeInUrl || codeInLocalStorage;
  if (! inviteCode) return;

  useAccountSearchApi()
    .getAccountByInviteCode({
      inviteCode,
    })
    .then( res => {
      const id = res.data.payload.idValue.raw;
      const name = res.data.payload.title;

      sponsorField.value = id;
      sponsorField.disabled = true;
      sponsorField.title = `${t('properties_custom.sponsor_id')}: ${name}`;
    })
    .catch( err => {
      console.error('getAccountByInviteCode error:', err);
    });

}

function assertPropsValid() {
  const confirmConfig = props.otpConfirm;
  if (! confirmConfig)
    throw new Error('otpConfirm prop is required');

  const allAliases = props.steps?.flatMap(s => s.aliases) ?? [];
  const hasAlias = (alias: string) => allAliases.includes(alias);

  const needsConfirm = Object.entries(confirmConfig).some(([alias, confMode]) => (confMode === true) && hasAlias(alias) && !isFieldOptional(alias));

  if (! needsConfirm)
    throw new Error('Not even one mandatory confirmable field in props!');
}

function detectBrowserLanguage(): string | undefined {
  const nav = window.navigator as any;
  const raw: string | undefined = nav?.language || (Array.isArray(nav?.languages) ? nav.languages[0] : undefined);
  if (!raw) return undefined;
  const code = String(raw).toLowerCase();
  // normalize to short code (e.g., en, ru)
  const short = code.split('-')[0];
  return short;
}

function setupLanguageField() {
  const mode = props.languageMode;
  if (!mode) return;

  const lang = detectBrowserLanguage();

  const allowedKeys = (() => {
    const langProp = usePropertiesStore().findPropertyDefinition('p', 'language_id');
    const dictKeys = langProp?.dictionary?.dictionaryItems?.map((i: any) => i.key) || [];
    if (dictKeys.length) return dictKeys as string[];
    return languagesFromApi.value.map(i => i.key);
  })();

  function matchLanguageKey(preferred?: string): string | undefined {
    if (!preferred) return undefined;
    const short = String(preferred).toLowerCase();
    if (allowedKeys.includes(short)) return short;
    const exact = allowedKeys.find(k => String(k).toLowerCase() === short);
    if (exact) return exact;
    const starts = allowedKeys.find(k => String(k).toLowerCase().startsWith(short + '-'));
    if (starts) return starts;
    return undefined;
  }

  const field = stepper.findFormField('language_id');
  if (field) {
    if (mode === 'hidden_no_api') {
      field.hidden = true;
      field.disabled = true;
      return;
    }
    if (mode === 'hidden_auto') {
      field.hidden = true;
      field.disabled = true;
      const matched = matchLanguageKey(lang);
      if (matched) field.value = matched;
      return;
    }
    if (mode === 'editable_auto') {
      field.hidden = false;
      field.disabled = false;
      const matched = matchLanguageKey(lang);
      if (matched && (field.value === undefined || field.value === null || field.value === ''))
        field.value = matched;
      return;
    }
  } else {
    // Field not present in steps
    if (mode === 'editable_auto') {
      const firstFormStep = stepperProps.steps.find(s => s.type === 'FormBuilder');
      if (firstFormStep) {
        const newField = {
          ... genFormFieldFromAlias('language_id'),
          isMandatory: ! isFieldOptional('language_id'),
          hidden: false,
          disabled: false,
          value: matchLanguageKey(lang),
        };
        firstFormStep.form.props.modelValue = [ ...firstFormStep.form.props.modelValue, newField ];
      }
    }
  }
}

function withLanguageInProfile(profile: Record<string, any>) {
  const mode = props.languageMode;
  if (mode === 'hidden_no_api') return profile;

  // If field exists among steps, use that value; otherwise use detected
  const langField = stepper.findFormField('language_id');
  const current = langField?.value;
  const detected = detectBrowserLanguage();
  const value = current || detected;

  if (mode === 'editable_auto' || mode === 'hidden_auto') {
    const langProp = usePropertiesStore().findPropertyDefinition('p', 'language_id');
    const dictKeys = langProp?.dictionary?.dictionaryItems?.map((i: any) => i.key) || [];
    const allowed = dictKeys.length ? dictKeys as string[] : languagesFromApi.value.map(i => i.key);
    function pickAllowed(val?: string) {
      if (!val) return undefined;
      const lower = String(val).toLowerCase();
      if (allowed.includes(lower)) return lower;
      const exact = allowed.find(k => String(k).toLowerCase() === lower);
      if (exact) return exact;
      const starts = allowed.find(k => String(k).toLowerCase().startsWith(lower + '-'));
      if (starts) return starts;
      return undefined;
    }
    const matched = pickAllowed(value);
    if (matched) profile['language_id'] = matched;
  }
  return profile;
}
</script>
